// Host-side klient iframe integrace (§21B). Poskytuje stejné logické API jako
// přímé SDK, ale volání posílá přes postMessage do iframe s viewerem a vrací
// Promisy. Nezávisí na thatopen/three — je to tenká, lehká vrstva pro hostitele.
// targetOrigin je POVINNÝ (v produkci nikdy "*").

const TAG = "__ifcsdk";

// Metody vracející Promise (asynchronní na straně vieweru).
const ASYNC = new Set([
  "loadModel", "loadStream", "select", "selectByGlobalId", "getProperties", "find",
  "getSpatialTree", "setState", "setColor", "highlight",
]);

export class IFCViewerIframeClient {
  // opts: { iframe: HTMLIFrameElement, targetOrigin: string }
  constructor(opts = {}) {
    if (!opts.iframe) throw new Error("IFCViewerIframeClient: 'iframe' je povinný.");
    if (!opts.targetOrigin) throw new Error("IFCViewerIframeClient: 'targetOrigin' je povinný (nepoužívejte '*').");
    this._iframe = opts.iframe;
    this._origin = opts.targetOrigin;
    this._seq = 0;
    this._pending = new Map();
    this._handlers = new Map();
    this._ready = false;
    this._readyWaiters = [];
    window.addEventListener("message", (e) => this._onMessage(e));
    this._startHandshake();
  }

  // Aktivně pingujeme iframe, dokud nedorazí „ready" — řeší závod, kdy bridge
  // odešle ready dřív, než jsme připraveni naslouchat, i opačně.
  _startHandshake() {
    const ping = () => {
      if (this._ready) return;
      const win = this._iframe.contentWindow;
      if (win) { try { win.postMessage({ [TAG]: 1, type: "connect" }, this._origin); } catch (_) {} }
    };
    this._pingTimer = setInterval(ping, 250);
    if (this._iframe.addEventListener) this._iframe.addEventListener("load", ping);
    ping();
    setTimeout(() => { if (this._pingTimer) clearInterval(this._pingTimer); }, 15000);
  }

  _onMessage(e) {
    const data = e.data;
    if (!data || data[TAG] == null) return;
    if (this._origin !== "*" && e.origin !== this._origin) return;
    if (data.type === "ready") {
      if (this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null; }
      if (this._ready) return;                 // ready může přijít víckrát (ping)
      this._ready = true;
      this._readyWaiters.splice(0).forEach((r) => r(data));
      this._emitLocal("ready", data);
    } else if (data.type === "result") {
      const p = this._pending.get(data.id);
      if (p) { this._pending.delete(data.id); data.ok ? p.resolve(data.value) : p.reject(data.error); }
    } else if (data.type === "event") {
      this._emitLocal(data.event, data.payload);
    }
  }

  ready() {
    if (this._ready) return Promise.resolve();
    return new Promise((res) => this._readyWaiters.push(res));
  }

  call(method, ...args) {
    const id = "c" + (++this._seq);
    const win = this._iframe.contentWindow;
    if (!win) return Promise.reject(new Error("iframe není připraven."));
    win.postMessage({ [TAG]: 1, type: "call", id, method, args }, this._origin);
    return new Promise((resolve, reject) => this._pending.set(id, { resolve, reject }));
  }

  // Lokální event registr (události přeposlané z vieweru).
  on(event, handler) {
    if (!this._handlers.has(event)) this._handlers.set(event, new Set());
    this._handlers.get(event).add(handler);
    return () => this.off(event, handler);
  }
  off(event, handler) { const s = this._handlers.get(event); if (s) s.delete(handler); }
  _emitLocal(event, payload) {
    const s = this._handlers.get(event);
    if (s) for (const h of Array.from(s)) { try { h(payload); } catch (_) {} }
  }

  // Zrcadlo veřejného API — vše přes postMessage. Vrací Promisy.
  loadModel(source, opts) { return this.call("loadModel", source, opts); }
  loadStream(source, opts) { return this.call("loadStream", source, opts); }
  removeModel(id) { return this.call("removeModel", id); }
  showModel(id) { return this.call("showModel", id); }
  hideModel(id) { return this.call("hideModel", id); }
  listModels() { return this.call("listModels"); }
  select(objects, opts) { return this.call("select", objects, opts); }
  selectByGlobalId(modelId, globalId, opts) { return this.call("selectByGlobalId", modelId, globalId, opts); }
  clearSelection() { return this.call("clearSelection"); }
  getSelection() { return this.call("getSelection"); }
  highlight(objects, opts) { return this.call("highlight", objects, opts); }
  setColor(objects, color) { return this.call("setColor", objects, color); }
  resetColors() { return this.call("resetColors"); }
  show(objects) { return this.call("show", objects); }
  hide(objects) { return this.call("hide", objects); }
  isolate(objects) { return this.call("isolate", objects); }
  showAll() { return this.call("showAll"); }
  zoomTo(objects) { return this.call("zoomTo", objects); }
  fitToModel() { return this.call("fitToModel"); }
  getBounds(modelId) { return this.call("getBounds", modelId); }
  getStoreys(modelId) { return this.call("getStoreys", modelId); }
  setView(name) { return this.call("setView", name); }
  getCameraState() { return this.call("getCameraState"); }
  setCameraState(state, animate) { return this.call("setCameraState", state, animate); }
  getProperties(object) { return this.call("getProperties", object); }
  find(query) { return this.call("find", query); }
  getSpatialTree(modelId, options) { return this.call("getSpatialTree", modelId, options); }
  getState() { return this.call("getState"); }
  setState(state) { return this.call("setState", state); }
  setTheme(theme) { return this.call("setTheme", theme); }
  // Řezy přes tečkovou cestu.
  get sections() {
    const call = this.call.bind(this);
    return {
      enable: () => call("sections.enable"),
      disable: () => call("sections.disable"),
      create: (o) => call("sections.create", o),
      remove: (id) => call("sections.remove", id),
      clear: () => call("sections.clear"),
      list: () => call("sections.list"),
    };
  }
}

export default IFCViewerIframeClient;
